import React, { useEffect, useState, useRef } from 'react';
import { useCategories } from '../../Contexts/CategoryContext';
import { useCompany } from '../../Contexts/CompanyContext';
import { useBrands } from '../../Contexts/BrandContext';
import {
    Product,
    getProductDisplayInfo,
    groupProductsForListing,
    ProductFilters,
    SortOption
} from '../../Services/productService';
import { Brand } from '../../Services/brandService';
import { useNavigate } from 'react-router-dom';
import { useHomeShopProducts, useAvailableTags } from '../../Hooks/useProductQuery';
import ProductImage from '../shared/ProductImage';

// ─── Max visible category tabs (All Products + up to 10 categories + View All = 12) ──
const MAX_CATEGORY_TABS = 10; // excludes "All Products" and "View All"

// ─── Helper: open the SideDrawer from anywhere ───────────────────────────────
const openSideDrawer = () => window.dispatchEvent(new Event('open-side-drawer'));

const ProductImageWithPlaceholder = ProductImage;

const HomeShop: React.FC = () => {
    // ── Filter state ───────────────────────────────────────────────────────────
    const [selectedCategory, setSelectedCategory] = useState<string>('*');
    const [showFilter, setShowFilter] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeSearchQuery, setActiveSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<SortOption>('newness');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    const { activeParentCategories, loading: categoriesLoading } = useCategories();
    const { formatPrice, displayPrices } = useCompany();
    const { allBrands } = useBrands();

    const searchPanelRef = useRef<HTMLDivElement>(null);
    const filterPanelRef = useRef<HTMLDivElement>(null);
    const gridRef        = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();

    // ── Build filter object ────────────────────────────────────────────────────
    const filters: ProductFilters = {
        categoryId:  selectedCategory !== '*' ? selectedCategory : undefined,
        searchQuery: activeSearchQuery || undefined,
        tags:        selectedTags.length ? selectedTags : undefined,
        sortBy,
    };

    // ── React Query: products ──────────────────────────────────────────────────
    const { data: productsData, isLoading: productsLoading } = useHomeShopProducts(filters);

    const products: Product[] = productsData?.products ?? [];
    const hasMore: boolean    = productsData?.hasMore  ?? false;

    // Collapse identical brand-new units (same brand+model+trim+color) into a
    // single card with a count — pre-owned units are never grouped.
    const listingItems = React.useMemo(() => groupProductsForListing(products), [products]);

    // ── React Query: tags ──────────────────────────────────────────────────────
    const { data: availableTags = [] } = useAvailableTags();

    // ── Slice categories: show up to MAX_CATEGORY_TABS, always append View All ─
    const visibleCategories = activeParentCategories.slice(0, MAX_CATEGORY_TABS);
    const hasHiddenCategories = activeParentCategories.length > MAX_CATEGORY_TABS;
    // Show "View All" if there are more than MAX, OR always (so user can browse all brands/categories in drawer)
    const showViewAll = true;

    // ── IntersectionObserver: staggered card entrance ─────────────────────────
    useEffect(() => {
        if (!gridRef.current || products.length === 0) return;

        const isMobile = window.innerWidth <= 768;
        const cards    = gridRef.current.querySelectorAll<HTMLElement>('.hs-card');

        if (isMobile) {
            cards.forEach(card => card.classList.add('hs-card--visible'));
            return;
        }

        cards.forEach(card => card.classList.remove('hs-card--visible'));

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('hs-card--visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.01, rootMargin: '0px 0px 0px 0px' }
        );

        cards.forEach((card) => observer.observe(card));

        const fallback = setTimeout(() => {
            cards.forEach(card => card.classList.add('hs-card--visible'));
        }, 800);

        return () => {
            observer.disconnect();
            clearTimeout(fallback);
        };
    }, [products]);

    // ── Handlers ───────────────────────────────────────────────────────────────
    const handleLoadMore = () => navigate('/shop');

    const handleCategoryClick = (categoryId: string) => {
        setSearchQuery('');
        setActiveSearchQuery('');
        setSelectedTags([]);
        setSelectedCategory(categoryId);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setActiveSearchQuery(searchQuery);
    };

    const toggleTag = (tag: string) => {
        setSelectedTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    };

    const handleProductClick = (productId: string, count: number = 1) => {
        navigate(`/product/${productId}`, count > 1 ? { state: { availableCount: count } } : undefined);
    };

    const handleSearchToggle = () => {
        if (showFilter) setShowFilter(false);
        setShowSearch(!showSearch);
    };

    const handleFilterToggle = () => {
        if (showSearch) setShowSearch(false);
        setShowFilter(!showFilter);
    };

    const imageContainerStyle: React.CSSProperties = {
        position:        'relative',
        width:           '100%',
        paddingBottom:   '125%',
        overflow:        'hidden',
        backgroundColor: '#f8f9fa'
    };

    return (
        <div>
            <style>{`
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-40px); max-height: 0; }
                    to   { opacity: 1; transform: translateY(0);     max-height: 600px; }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(30px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes fadeInLeft {
                    from { opacity: 0; transform: translateX(-40px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes fadeInScale {
                    from { opacity: 0; transform: scale(0.85); }
                    to   { opacity: 1; transform: scale(1); }
                }
                @keyframes shimmer {
                    0%   { background-position: -1000px 0; }
                    100% { background-position:  1000px 0; }
                }
                .panel-enter { animation: slideDown 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .search-panel-wrapper,
                .filter-panel-wrapper { overflow: hidden; width: 100%; }
                .search-input-container { animation: fadeInScale 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .search-input {
                    animation: shimmer 3s infinite;
                    background: linear-gradient(to right, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%);
                    background-size: 1000px 100%;
                }
                .filter-sort-section { animation: fadeInLeft 0.9s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .filter-tags-section { animation: fadeInLeft 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
                .filter-tag-item  { animation: fadeInUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
                .filter-sort-item { animation: fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; opacity: 0; }
                .filter-tag-item:nth-child(1)    { animation-delay: 0.1s; }
                .filter-tag-item:nth-child(2)    { animation-delay: 0.2s; }
                .filter-tag-item:nth-child(3)    { animation-delay: 0.3s; }
                .filter-tag-item:nth-child(4)    { animation-delay: 0.4s; }
                .filter-tag-item:nth-child(5)    { animation-delay: 0.5s; }
                .filter-tag-item:nth-child(6)    { animation-delay: 0.6s; }
                .filter-tag-item:nth-child(7)    { animation-delay: 0.7s; }
                .filter-tag-item:nth-child(8)    { animation-delay: 0.8s; }
                .filter-tag-item:nth-child(9)    { animation-delay: 0.9s; }
                .filter-tag-item:nth-child(10)   { animation-delay: 1.0s; }
                .filter-tag-item:nth-child(n+11) { animation-delay: 1.1s; }
                .filter-sort-item:nth-child(1)   { animation-delay: 0.2s; }
                .filter-sort-item:nth-child(2)   { animation-delay: 0.4s; }
                .filter-sort-item:nth-child(3)   { animation-delay: 0.6s; }
                .icon-rotate { transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
                .icon-rotate.active { transform: rotate(180deg); }
                .filter-tag-item { transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); }
                .filter-tag-item:hover { transform: translateY(-3px); box-shadow: 0 6px 12px rgba(0,0,0,0.15); }

                /* ── Sold Out corner badge ── */
                .sold-out-badge-corner {
                    position: absolute;
                    top: 12px; left: 12px;
                    z-index: 5;
                    background: #e84040;
                    color: #fff;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                    padding: 5px 13px;
                    border-radius: 30px;
                    text-transform: uppercase;
                    pointer-events: none;
                    box-shadow: 0 2px 8px rgba(232, 64, 64, 0.35);
                }

                /* ── Grouped "N identical brand-new units" badge ── */
                .available-count-badge-corner {
                    position: absolute;
                    top: 12px; left: 12px;
                    z-index: 5;
                    background: #2f9e44;
                    color: #fff;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                    padding: 5px 13px;
                    border-radius: 30px;
                    text-transform: uppercase;
                    pointer-events: none;
                    box-shadow: 0 2px 8px rgba(47, 158, 68, 0.35);
                }

                /* ── Discount styles ── */
                .price-original {
                    text-decoration: line-through;
                    color: #aaa;
                    font-size: 13px;
                    margin-left: 6px;
                    font-weight: 400;
                }
                .price-discounted { color: inherit; font-weight: 600; }
                .price-wrapper {
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 2px;
                }
                .discount-badge {
                    display: inline-block;
                    background: #e74c3c;
                    color: #fff;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 0.8px;
                    padding: 2px 7px;
                    border-radius: 2px;
                    margin-left: 6px;
                    vertical-align: middle;
                    line-height: 1.6;
                }

                /* ── Brand label ── */
                .product-card-brand {
                    display: block;
                    margin-top: 5px;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 1.4px;
                    text-transform: uppercase;
                    color: #717fe0;
                    line-height: 1.4;
                }

                /* ── Product card staggered animation ── */
                .hs-card {
                    opacity: 0;
                    transform: translateY(32px);
                    transition:
                        opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1),
                        transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .hs-card.hs-card--visible {
                    opacity: 1;
                    transform: translateY(0);
                }
                .hs-card:nth-child(4n+1) { transition-delay: 0ms; }
                .hs-card:nth-child(4n+2) { transition-delay: 80ms; }
                .hs-card:nth-child(4n+3) { transition-delay: 160ms; }
                .hs-card:nth-child(4n+4) { transition-delay: 240ms; }

                @media (max-width: 768px) {
                    .hs-card {
                        opacity: 1 !important;
                        transform: none !important;
                        transition: none !important;
                    }
                }

                /* ── Product name underline on hover ── */
                .hs-product-name {
                    position: relative;
                    display: inline-block;
                }
                .hs-product-name::after {
                    content: '';
                    position: absolute;
                    bottom: 0; left: 0;
                    width: 0; height: 1px;
                    background: #717fe0;
                    transition: width 0.3s ease;
                }
                .hs-product-name:hover::after { width: 100%; }

                /* ── Sold out badge pulse ── */
                @keyframes hs-sold-pulse {
                    0%, 100% { box-shadow: 0 2px 8px rgba(232,64,64,0.35); }
                    50%      { box-shadow: 0 2px 16px rgba(232,64,64,0.65); }
                }
                .hs-sold-pulse { animation: hs-sold-pulse 2.4s ease-in-out infinite; }

                /* ── Discount badge pop-in ── */
                @keyframes hs-badge-pop {
                    0%   { transform: scale(0.5); opacity: 0; }
                    70%  { transform: scale(1.15); }
                    100% { transform: scale(1); opacity: 1; }
                }
                .hs-discount-pop {
                    animation: hs-badge-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
                }

                /* ── Load More button entrance ── */
                .hs-loadmore {
                    opacity: 0;
                    transform: translateY(16px);
                    transition: opacity 0.6s ease, transform 0.6s ease;
                }
                .hs-loadmore.hs-loadmore--visible {
                    opacity: 1;
                    transform: translateY(0);
                }

                /* ── Category filter tab active underline ── */
                .hs-cat-btn {
                    position: relative;
                }
                .hs-cat-btn::after {
                    content: '';
                    position: absolute;
                    bottom: -2px; left: 0;
                    width: 0; height: 2px;
                    background: #333;
                    transition: width 0.3s ease;
                }
                .hs-cat-btn.how-active1::after { width: 100%; }

                /* ── View All button ── */
                .hs-view-all-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;
                    color: #717fe0;
                    background: none;
                    font-size: 12px; 
                    line-height: 1;
                    cursor: pointer;
                    transition: background 0.25s ease, color 0.25s ease, transform 0.2s ease;
                    white-space: nowrap;
                    margin-left: 4px;
                }
                .hs-view-all-btn:hover {
                    transform: translateY(-1px);
                }
                .hs-view-all-btn svg {
                    transition: transform 0.25s ease;
                }
                .hs-view-all-btn:hover svg {
                    transform: translateX(3px);
                }

                /* ── Mobile grid: 2 columns ── */
                @media (max-width: 576px) {
                    .isotope-grid .col-sm-6 {
                        width: 50% !important;
                        padding-left: 8px !important;
                        padding-right: 8px !important;
                        padding-bottom: 20px !important;
                    }
                }
            `}</style>

            <div className="bg0 m-t-23 p-b-0">
                <div className="container">

                    {/* ================= CATEGORY + FILTER + SEARCH ================= */}
                    <div className="flex-w flex-sb-m p-b-52">

                        {/* ── Category tabs (max 10) + View All ── */}
                        <div className="flex-w flex-l-m filter-tope-group m-tb-10" style={{ alignItems: 'center', flexWrap: 'wrap', gap: '0 4px' }}>
                            {/* All Products */}
                            <button
                                className={`hs-cat-btn stext-106 cl6 hov1 bor3 trans-04 m-r-32 m-tb-5 ${selectedCategory === '*' ? 'how-active1' : ''}`}
                                onClick={() => handleCategoryClick('*')}
                            >
                                All Products
                            </button>

                            {/* Up to MAX_CATEGORY_TABS categories */}
                            {categoriesLoading ? (
                                <span className="stext-106 cl6 m-r-32 m-tb-5">Loading categories...</span>
                            ) : (
                                visibleCategories.map(category => (
                                    <button
                                        key={category.id}
                                        className={`hs-cat-btn stext-106 cl6 hov1 bor3 trans-04 m-r-32 m-tb-5 ${selectedCategory === category.id ? 'how-active1' : ''}`}
                                        onClick={() => handleCategoryClick(category.id!)}
                                    >
                                        {category.name}
                                    </button>
                                ))
                            )}

                            {/* View All — always visible, opens Side Drawer */}
                            {!categoriesLoading && (
                                <button
                                    className="hs-view-all-btn m-tb-5"
                                    onClick={openSideDrawer}
                                    title="Browse all categories and brands"
                                >
                                    View All
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                        <polyline points="12 5 19 12 12 19" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* ── Filter + Search toggles ── */}
                        <div className="flex-w flex-c-m m-tb-10">
                            <button
                                className="flex-c-m stext-106 cl6 size-104 bor4 pointer hov-btn3 trans-04 m-r-8 m-tb-4"
                                onClick={handleFilterToggle}
                            >
                                <i className={`cl2 m-r-6 fs-15 trans-04 zmdi icon-rotate ${showFilter ? 'zmdi-close active' : 'zmdi-filter-list'}`}></i>
                                Filter
                            </button>
                            <button
                                className="flex-c-m stext-106 cl6 size-105 bor4 pointer hov-btn3 trans-04 m-tb-4"
                                onClick={handleSearchToggle}
                            >
                                <i className={`cl2 m-r-6 fs-15 trans-04 zmdi icon-rotate ${showSearch ? 'zmdi-close active' : 'zmdi-search'}`}></i>
                                Search
                            </button>
                        </div>

                        {/* ── Search panel ── */}
                        {showSearch && (
                            <div ref={searchPanelRef} className="search-panel-wrapper panel-enter">
                                <div className="w-full p-t-10 p-b-15">
                                    <form onSubmit={handleSearchSubmit}>
                                        <div className="bor8 dis-flex p-l-15 search-input-container">
                                            <button type="submit" className="size-113 flex-c-m fs-16 cl2 hov-cl1 trans-04">
                                                <i className="zmdi zmdi-search"></i>
                                            </button>
                                            <input
                                                className="mtext-107 cl2 size-114 plh2 p-r-15 search-input"
                                                type="text"
                                                placeholder="Search products... (Press Enter)"
                                                value={searchQuery}
                                                onChange={e => setSearchQuery(e.target.value)}
                                                autoFocus
                                            />
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* ── Filter panel ── */}
                        {showFilter && (
                            <div ref={filterPanelRef} className="filter-panel-wrapper panel-enter">
                                <div className="w-full p-t-10">
                                    <div className="wrap-filter flex-w bg6 w-full p-lr-40 p-t-27 p-b-27 p-lr-15-sm">
                                        <div className="filter-col1 p-r-15 p-b-27 filter-sort-section">
                                            <div className="mtext-102 cl2 p-b-15">Sort By</div>
                                            <ul>
                                                {(['newness', 'price-low', 'price-high'] as SortOption[]).map(option => (
                                                    <li key={option} className="p-b-6 filter-sort-item">
                                                        <button
                                                            onClick={() => setSortBy(option as SortOption)}
                                                            className={`filter-link stext-106 trans-04 ${sortBy === option ? 'filter-link-active' : ''}`}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                                                        >
                                                            {option === 'newness' ? 'Newness' : option === 'price-low' ? 'Price: Low to High' : 'Price: High to Low'}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="filter-col4 p-b-27 filter-tags-section" style={{ flex: 1 }}>
                                            <div className="mtext-102 cl2 p-b-15">Tags</div>
                                            {availableTags.length > 0 ? (
                                                <div className="flex-w p-t-4 m-r--5">
                                                    {availableTags.map(tag => (
                                                        <button
                                                            key={tag}
                                                            onClick={() => toggleTag(tag)}
                                                            className={`flex-c-m stext-107 cl6 size-301 bor7 p-lr-15 trans-04 m-r-5 m-b-5 filter-tag-item ${selectedTags.includes(tag) ? 'hov-tag1' : ''}`}
                                                            style={{
                                                                backgroundColor: selectedTags.includes(tag) ? '#333' : 'transparent',
                                                                color: selectedTags.includes(tag) ? '#fff' : 'inherit',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            {tag}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : <p className="stext-107 cl6">No tags available</p>}
                                            {selectedTags.length > 0 && (
                                                <button
                                                    onClick={() => setSelectedTags([])}
                                                    className="stext-106 cl6 hov1 trans-04 m-t-15"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                                >
                                                    Clear all tags
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ================= PRODUCTS GRID ================= */}
                    <div className="row isotope-grid" ref={gridRef}>
                        {listingItems.map(({ product, count }) => {
                            const displayInfo = getProductDisplayInfo(product);
                            const isSoldOut   = displayInfo.isOutOfStock;

                            const brandName = product.brandId
                                ? allBrands?.find((b: Brand) => b.id === product.brandId)?.name ?? null
                                : null;

                            return (
                                <div key={product.id} className="col-sm-6 col-md-4 col-lg-3 p-b-35 isotope-item hs-card">
                                    <div className="block2">
                                        <div className="block2-pic hov-img0" style={imageContainerStyle}>
                                            <ProductImageWithPlaceholder
                                                src={product.images?.[0]}
                                                alt={product.name}
                                            />

                                            <div className="block2-overlay" />

                                            {isSoldOut && (
                                                <div className="sold-out-badge-corner hs-sold-pulse">
                                                    SOLD
                                                </div>
                                            )}

                                            {!isSoldOut && count > 1 && (
                                                <div className="available-count-badge-corner">{count} AVAILABLE</div>
                                            )}

                                            <a
                                                href="#"
                                                className="block2-btn flex-c-m stext-103 cl2 size-102 bg0 bor2 hov-btn1 p-lr-15 trans-04"
                                                onClick={(e) => { e.preventDefault(); handleProductClick(product.id, count); }}
                                            >
                                                View Product
                                            </a>
                                        </div>

                                        <div className="block2-txt flex-w flex-t p-t-14">
                                            <div className="block2-txt-child1 flex-col-l" style={{ width: '100%' }}>
                                                <a
                                                    href="#"
                                                    className="hs-product-name stext-104 cl4 hov-cl1 trans-04 p-b-6"
                                                    onClick={(e) => { e.preventDefault(); handleProductClick(product.id, count); }}
                                                >
                                                    {product.name}
                                                </a>

                                                <div className="price-wrapper">
                                                    {!displayPrices ? (
                                                        <span className="stext-105 cl3">Contact for price</span>
                                                    ) : displayInfo.hasDiscount ? (
                                                        <>
                                                            <span className="stext-105 cl3 price-discounted">
                                                                {formatPrice(displayInfo.discountedPrice)}
                                                            </span>
                                                            <span className="price-original">
                                                                {formatPrice(displayInfo.price)}
                                                            </span>
                                                            <span className="discount-badge hs-discount-pop">
                                                                -{displayInfo.discount}%
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="stext-105 cl3">
                                                            {formatPrice(displayInfo.price)}
                                                        </span>
                                                    )}
                                                </div>

                                                {brandName && (
                                                    <span className="product-card-brand">{brandName}</span>
                                                )}

                                                {product.trimName && (
                                                    <span className="stext-107 cl6" style={{ fontSize: '12px', marginTop: '4px' }}>
                                                        {product.trimName}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* ================= LOAD MORE / MESSAGES ================= */}
                    {productsLoading && (
                        <div className="flex-c-m w-full p-t-45">
                            <span className="stext-101 cl5">Loading...</span>
                        </div>
                    )}
                    {!productsLoading && hasMore && products.length > 0 && (
                        <div
                            className="flex-c-m flex-w w-full p-t-45 hs-loadmore"
                            ref={el => {
                                if (el) {
                                    const o = new IntersectionObserver(([e]) => {
                                        if (e.isIntersecting) {
                                            el.classList.add('hs-loadmore--visible');
                                            o.disconnect();
                                        }
                                    }, { threshold: 0.5 });
                                    o.observe(el);
                                }
                            }}
                        >
                            <a
                                href="#"
                                className="flex-c-m stext-101 cl5 size-103 bg2 bor1 hov-btn1 p-lr-15 trans-04"
                                onClick={(e) => { e.preventDefault(); handleLoadMore(); }}
                            >
                                Load More
                            </a>
                        </div>
                    )}
                    {!productsLoading && !hasMore && products.length > 0 && (
                        <div className="flex-c-m w-full p-t-45">
                            <span className="stext-101 cl5"></span>
                        </div>
                    )}
                    {!productsLoading && products.length === 0 && (
                        <div className="flex-c-m w-full p-t-45">
                            <span className="stext-101 cl5">No products found</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HomeShop;